import {DifficultSummary} from '@domain/pom'

import {MonthlyHierarchyAnalysisDataPoint, YearlyHierarchyAnalysisDataPoint} from '../types'

export const parseMonthToTimestamp = (month: string, currentYear: number): number => {
  const numericMonth = Number(month)
  if (!Number.isNaN(numericMonth) && numericMonth >= 1 && numericMonth <= 12) {
    return new Date(currentYear, numericMonth - 1, 1).getTime()
  }

  const parsed = Date.parse(month)
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed
}

export const synthesizeTodayYearlyPoints = (
  currentYearMonthlyPoints: MonthlyHierarchyAnalysisDataPoint[],
  currentYear: number
): YearlyHierarchyAnalysisDataPoint[] => {
  if (currentYearMonthlyPoints.length === 0) return []

  const latestByHierarchyId = new Map<string, MonthlyHierarchyAnalysisDataPoint>()

  for (const dp of currentYearMonthlyPoints) {
    const existing = latestByHierarchyId.get(dp.hierarchyId)
    const dpTime = parseMonthToTimestamp(dp.month, currentYear)
    const existingTime = existing
      ? parseMonthToTimestamp(existing.month, currentYear)
      : Number.NEGATIVE_INFINITY

    if (!existing || dpTime > existingTime) {
      latestByHierarchyId.set(dp.hierarchyId, dp)
    }
  }

  return Array.from(latestByHierarchyId.values()).map((dp) => ({
    hierarchyId: dp.hierarchyId,
    year: currentYear,
    totalScore: dp.totalScore ? {...dp.totalScore} : undefined,
    difficultyScores: dp.difficultyScores
      ? {
          basic: dp.difficultyScores.basic ? {...dp.difficultyScores.basic} : undefined,
          advanced: dp.difficultyScores.advanced ? {...dp.difficultyScores.advanced} : undefined,
          excellence: dp.difficultyScores.excellence
            ? {...dp.difficultyScores.excellence}
            : undefined
        }
      : undefined
  }))
}

export const mergeYearlyWithTodayPoints = (
  yearlyDataPoints: YearlyHierarchyAnalysisDataPoint[],
  synthesizedTodayYearlyPoints: YearlyHierarchyAnalysisDataPoint[],
  currentYear: number
): YearlyHierarchyAnalysisDataPoint[] => {
  const hasCurrentYear = yearlyDataPoints.some((dp) => dp.year === currentYear)
  if (hasCurrentYear) return yearlyDataPoints
  return [...yearlyDataPoints, ...synthesizedTodayYearlyPoints]
}

export const accumulateSummary = (
  existing?: DifficultSummary,
  incoming?: DifficultSummary
): DifficultSummary | undefined => {
  if (!existing && !incoming) return undefined
  const ap = (existing?.answeredPositively ?? 0) + (incoming?.answeredPositively ?? 0)
  const qc = (existing?.questionCount ?? 0) + (incoming?.questionCount ?? 0)
  return {
    answeredPositively: ap,
    questionCount: qc,
    positivePercent: qc > 0 ? Math.round((ap / qc) * 100) : 0
  }
}

/** Aggregates multi-area monthly data into a single 'global' entry per month */
export const aggregateMonthlyData = (
  data: MonthlyHierarchyAnalysisDataPoint[]
): MonthlyHierarchyAnalysisDataPoint[] => {
  const byMonth = new Map<string, MonthlyHierarchyAnalysisDataPoint>()
  for (const dp of data) {
    const key = dp.month.substring(0, 7)
    const existing = byMonth.get(key)
    if (!existing) {
      byMonth.set(key, {...dp, hierarchyId: 'global'})
    } else {
      if (existing.totalScore && dp.totalScore) {
        const ap = existing.totalScore.answeredPositively + dp.totalScore.answeredPositively
        const qc = existing.totalScore.questionCount + dp.totalScore.questionCount
        existing.totalScore = {
          answeredPositively: ap,
          questionCount: qc,
          positivePercent: qc > 0 ? Math.round((ap / qc) * 100) : 0
        }
      }
      if (dp.difficultyScores) {
        existing.difficultyScores = {
          basic: accumulateSummary(existing.difficultyScores?.basic, dp.difficultyScores.basic),
          advanced: accumulateSummary(
            existing.difficultyScores?.advanced,
            dp.difficultyScores.advanced
          ),
          excellence: accumulateSummary(
            existing.difficultyScores?.excellence,
            dp.difficultyScores.excellence
          )
        }
      }
    }
  }
  return Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month))
}

/** Aggregates multi-area yearly data into a single 'global' entry per year */
export const aggregateYearlyData = (
  data: YearlyHierarchyAnalysisDataPoint[]
): YearlyHierarchyAnalysisDataPoint[] => {
  const byYear = new Map<number, YearlyHierarchyAnalysisDataPoint>()
  for (const dp of data) {
    const existing = byYear.get(dp.year)
    if (!existing) {
      byYear.set(dp.year, {...dp, hierarchyId: 'global'})
    } else {
      if (existing.totalScore && dp.totalScore) {
        const ap = existing.totalScore.answeredPositively + dp.totalScore.answeredPositively
        const qc = existing.totalScore.questionCount + dp.totalScore.questionCount
        existing.totalScore = {
          answeredPositively: ap,
          questionCount: qc,
          positivePercent: qc > 0 ? Math.round((ap / qc) * 100) : 0
        }
      }
      if (dp.difficultyScores) {
        existing.difficultyScores = {
          basic: accumulateSummary(existing.difficultyScores?.basic, dp.difficultyScores.basic),
          advanced: accumulateSummary(
            existing.difficultyScores?.advanced,
            dp.difficultyScores.advanced
          ),
          excellence: accumulateSummary(
            existing.difficultyScores?.excellence,
            dp.difficultyScores.excellence
          )
        }
      }
    }
  }
  return Array.from(byYear.values()).sort((a, b) => a.year - b.year)
}
